import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, BarChart3, AlertTriangle, Settings, Search, Filter, ChevronDown,
  RefreshCw, Check, X, ArrowUpRight, ArrowDownLeft, Eye, Loader2,
  DollarSign, Users, Activity, TrendingUp, Shield, Lock, Unlock,
  CheckCircle2, XCircle, Clock, AlertCircle, Edit2, Plus, Minus,
  CreditCard, Building2, Bitcoin, ChevronLeft, ChevronRight
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL || '';
const adminApi = axios.create({ baseURL: `${API}/api` });
adminApi.interceptors.request.use(cfg => {
  const token = localStorage.getItem('admin_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const fmt = (n) => new Intl.NumberFormat('es-MX', { style:'currency', currency:'MXN' }).format(n || 0);
const fmtDate = (d) => { try { return format(parseISO(d), "d MMM yyyy HH:mm", {locale:es}); } catch { return d || '—'; } };
const PAGE_SIZE = 25;

function StatCard({ label, value, sub, icon: Icon, color, bg }) {
  return (
    <div style={{ background:'#fff', borderRadius:16, padding:'20px 22px', border:'1px solid #f3f4f6', boxShadow:'0 1px 4px rgba(0,0,0,0.05)', display:'flex', alignItems:'center', gap:14 }}>
      <div style={{ width:48, height:48, borderRadius:14, background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <Icon size={22} color={color}/>
      </div>
      <div>
        <p style={{ margin:0, fontSize:12, color:'#9ca3af', fontWeight:600 }}>{label}</p>
        <p style={{ margin:'2px 0 0', fontSize:22, fontWeight:900, color:'#111827' }}>{value}</p>
        {sub && <p style={{ margin:'2px 0 0', fontSize:11, color:'#6b7280' }}>{sub}</p>}
      </div>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    completed: { color:'#22c55e', bg:'#f0fdf4', label:'Completado' },
    pending:   { color:'#f59e0b', bg:'#fffbeb', label:'Pendiente' },
    failed:    { color:'#ef4444', bg:'#fef2f2', label:'Fallido' },
    disputed:  { color:'#8b5cf6', bg:'#f5f3ff', label:'Disputa' },
    reversed:  { color:'#6b7280', bg:'#f9fafb', label:'Revertido' },
    open:      { color:'#ef4444', bg:'#fef2f2', label:'Abierta' },
    under_review: { color:'#f59e0b', bg:'#fffbeb', label:'En revisión' },
    resolved:  { color:'#22c55e', bg:'#f0fdf4', label:'Resuelta' },
    rejected:  { color:'#6b7280', bg:'#f9fafb', label:'Rechazada' },
    active:    { color:'#22c55e', bg:'#f0fdf4', label:'Activa' },
    frozen:    { color:'#06b6d4', bg:'#ecfeff', label:'Congelada' },
    suspended: { color:'#ef4444', bg:'#fef2f2', label:'Suspendida' },
  };
  const m = map[s] || { color:'#9ca3af', bg:'#f9fafb', label: s };
  return <span style={{ display:'inline-block', padding:'2px 10px', borderRadius:20, background:m.bg, color:m.color, fontSize:11, fontWeight:700, textTransform:'capitalize' }}>{m.label}</span>;
}

// ─── Tabs ────────────────────────────────────
const TABS = [
  { id:'dashboard', label:'Dashboard', icon:BarChart3 },
  { id:'transactions', label:'Transacciones', icon:Activity },
  { id:'wallets', label:'Wallets', icon:Wallet },
  { id:'disputes', label:'Disputas', icon:AlertTriangle },
  { id:'settings', label:'Configuración', icon:Settings },
];

// ─── Resolve Dispute Modal ──────────────────
function ResolveDisputeModal({ dispute, onClose, onSuccess }) {
  const [status, setStatus] = useState('resolved');
  const [notes, setNotes] = useState('');
  const [refund, setRefund] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!notes.trim()) return;
    setLoading(true);
    try {
      await adminApi.patch(`/wallet/admin/disputes/${dispute.id}`, {
        status,
        resolution_notes: notes,
        refund_amount: refund ? parseFloat(refund) : 0,
      });
      toast.success('Disputa actualizada');
      onSuccess();
      onClose();
    } catch(e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:20, maxWidth:480, width:'100%', overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontWeight:800, fontSize:16, margin:0 }}>Resolver disputa #{dispute.id?.slice(0,8)}</h3>
          <button onClick={onClose} style={{ background:'#f3f4f6', border:'none', borderRadius:8, padding:'6px 8px', cursor:'pointer' }}><X size={16}/></button>
        </div>
        <div style={{ padding:'18px 22px', display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ background:'#fff7ed', borderRadius:10, padding:'12px 14px' }}>
            <div style={{ fontSize:12, color:'#92400e', fontWeight:700 }}>Motivo</div>
            <div style={{ fontSize:13, color:'#78350f', marginTop:2 }}>{dispute.dispute_type} — {dispute.description?.slice(0,100)}</div>
            <div style={{ fontSize:12, color:'#9ca3af', marginTop:4 }}>Monto: <strong>{fmt(dispute.transaction_amount)}</strong></div>
          </div>

          <div>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:8 }}>Acción</label>
            <div style={{ display:'flex', gap:8 }}>
              {[
                { id:'under_review', label:'En revisión', color:'#f59e0b' },
                { id:'resolved', label:'Resolver', color:'#22c55e' },
                { id:'rejected', label:'Rechazar', color:'#ef4444' },
              ].map(s => (
                <button key={s.id} onClick={() => setStatus(s.id)} style={{ flex:1, padding:'10px', border:`2px solid ${status===s.id?s.color:'#e5e7eb'}`, borderRadius:10, background:status===s.id?`${s.color}10`:'#fff', fontWeight:700, fontSize:12, color:status===s.id?s.color:'#6b7280', cursor:'pointer' }}>{s.label}</button>
              ))}
            </div>
          </div>

          {status === 'resolved' && (
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Monto de reembolso (MXN)</label>
              <input type="number" value={refund} onChange={e=>setRefund(e.target.value)} placeholder="0.00" max={dispute.transaction_amount}
                style={{ width:'100%', padding:'11px 13px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, outline:'none', boxSizing:'border-box' }} />
              <p style={{ fontSize:11, color:'#9ca3af', marginTop:4 }}>Deja en 0 si no hay reembolso. Máx: {fmt(dispute.transaction_amount)}</p>
            </div>
          )}

          <div>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Notas de resolución *</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Explica la decisión..." rows={3}
              style={{ width:'100%', padding:'11px 13px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:13, resize:'none', outline:'none', boxSizing:'border-box' }} />
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose} style={{ flex:1, padding:'12px', border:'1.5px solid #e5e7eb', borderRadius:10, background:'transparent', fontWeight:700, cursor:'pointer' }}>Cancelar</button>
            <button disabled={!notes.trim()||loading} onClick={submit}
              style={{ flex:2, padding:'12px', background:!notes.trim()?'#e5e7eb':'#f97316', color:'#fff', border:'none', borderRadius:10, fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              {loading ? <Loader2 size={16} className="animate-spin"/> : <Check size={16}/>} Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Wallet Action Modal ────────────────────
function WalletActionModal({ wallet, onClose, onSuccess }) {
  const [action, setAction] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!action || !reason.trim()) return;
    setLoading(true);
    try {
      await adminApi.post(`/wallet/admin/wallets/${wallet.id}/action`, {
        action, amount: amount ? parseFloat(amount) : 0, reason
      });
      toast.success('Acción aplicada');
      onSuccess();
      onClose();
    } catch(e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  const ACTIONS = [
    { id:'freeze', label:'Congelar wallet', color:'#06b6d4', icon:Lock, desc:'El wallet queda bloqueado. El titular no podrá operar.' },
    { id:'unfreeze', label:'Descongelar wallet', color:'#22c55e', icon:Unlock, desc:'Reactiva el wallet congelado.' },
    { id:'adjust', label:'Ajuste de saldo', color:'#f97316', icon:Edit2, desc:'Suma o resta saldo manualmente (requiere razón).' },
    { id:'suspend', label:'Suspender', color:'#ef4444', icon:Shield, desc:'Suspensión permanente por fraude u otros motivos graves.' },
  ];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:20, maxWidth:480, width:'100%', overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontWeight:800, fontSize:16, margin:0 }}>Gestionar wallet</h3>
          <button onClick={onClose} style={{ background:'#f3f4f6', border:'none', borderRadius:8, padding:'6px 8px', cursor:'pointer' }}><X size={16}/></button>
        </div>
        <div style={{ padding:'18px 22px', display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ background:'#f9fafb', borderRadius:10, padding:'10px 14px' }}>
            <div style={{ fontSize:12, color:'#9ca3af' }}>Wallet: <strong style={{ fontFamily:'monospace' }}>{wallet.id?.slice(0,16)}...</strong></div>
            <div style={{ fontSize:12, color:'#9ca3af' }}>Restaurante: <strong>{wallet.restaurant_id}</strong> · Saldo: <strong style={{ color:'#111827' }}>{fmt(wallet.balance)}</strong></div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {ACTIONS.map(a => (
              <button key={a.id} onClick={() => setAction(a.id)}
                style={{ padding:'12px', border:`2px solid ${action===a.id?a.color:'#e5e7eb'}`, borderRadius:12, background:action===a.id?`${a.color}10`:'#fff', cursor:'pointer', textAlign:'left' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <a.icon size={14} color={a.color}/>
                  <span style={{ fontWeight:700, fontSize:12, color:action===a.id?a.color:'#374151' }}>{a.label}</span>
                </div>
                <p style={{ fontSize:10, color:'#9ca3af', margin:0, lineHeight:1.4 }}>{a.desc}</p>
              </button>
            ))}
          </div>

          {action === 'adjust' && (
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Monto (positivo = sumar, negativo = restar)</label>
              <input type="number" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="-500 ó 1000"
                style={{ width:'100%', padding:'11px 13px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, outline:'none', boxSizing:'border-box' }} />
            </div>
          )}

          <div>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Razón / Motivo *</label>
            <input value={reason} onChange={e=>setReason(e.target.value)} placeholder="Motivo de la acción..."
              style={{ width:'100%', padding:'11px 13px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:13, outline:'none', boxSizing:'border-box' }} />
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose} style={{ flex:1, padding:'12px', border:'1.5px solid #e5e7eb', borderRadius:10, background:'transparent', fontWeight:700, cursor:'pointer' }}>Cancelar</button>
            <button disabled={!action||!reason.trim()||loading} onClick={submit}
              style={{ flex:2, padding:'12px', background:(!action||!reason.trim())?'#e5e7eb':'#f97316', color:'#fff', border:'none', borderRadius:10, fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              {loading ? <Loader2 size={16} className="animate-spin"/> : null} Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────
export default function AdminOkWallet() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalTxs, setTotalTxs] = useState(0);
  const [txPage, setTxPage] = useState(0);
  const [wallets, setWallets] = useState([]);
  const [totalWallets, setTotalWallets] = useState(0);
  const [walletPage, setWalletPage] = useState(0);
  const [disputes, setDisputes] = useState([]);
  const [totalDisputes, setTotalDisputes] = useState(0);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTx, setSearchTx] = useState('');
  const [filterTxStatus, setFilterTxStatus] = useState('');
  const [filterTxCat, setFilterTxCat] = useState('');
  const [filterDisputeStatus, setFilterDisputeStatus] = useState('');
  const [filterWalletStatus, setFilterWalletStatus] = useState('');
  const [resolveDispute, setResolveDispute] = useState(null);
  const [walletAction, setWalletAction] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editSettings, setEditSettings] = useState({});

  const fetchOverview = useCallback(async () => {
    try { const r = await adminApi.get('/wallet/admin/overview'); setOverview(r.data); } catch(e) { console.error(e); }
  }, []);

  const fetchTransactions = useCallback(async (page=0, search='', status='', cat='') => {
    try {
      const p = { limit: PAGE_SIZE, skip: page*PAGE_SIZE };
      if (search) p.search = search;
      if (status) p.status = status;
      if (cat) p.category = cat;
      const r = await adminApi.get('/wallet/admin/transactions', { params: p });
      setTransactions(r.data.transactions || []);
      setTotalTxs(r.data.total || 0);
    } catch(e) { console.error(e); }
  }, []);

  const fetchWallets = useCallback(async (page=0, status='') => {
    try {
      const p = { limit: PAGE_SIZE, skip: page*PAGE_SIZE };
      if (status) p.status = status;
      const r = await adminApi.get('/wallet/admin/wallets', { params: p });
      setWallets(r.data.wallets || []);
      setTotalWallets(r.data.total || 0);
    } catch(e) { console.error(e); }
  }, []);

  const fetchDisputes = useCallback(async (status='') => {
    try {
      const p = { limit: 50 };
      if (status) p.status = status;
      const r = await adminApi.get('/wallet/admin/disputes', { params: p });
      setDisputes(r.data.disputes || []);
      setTotalDisputes(r.data.total || 0);
    } catch(e) { console.error(e); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const r = await adminApi.get('/wallet/admin/settings');
      setSettings(r.data);
      setEditSettings(r.data);
    } catch(e) { console.error(e); }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchOverview(), fetchTransactions(), fetchWallets(), fetchDisputes(), fetchSettings()]);
    setLoading(false);
  }, [fetchOverview, fetchTransactions, fetchWallets, fetchDisputes, fetchSettings]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { fetchTransactions(txPage, searchTx, filterTxStatus, filterTxCat); }, [txPage, searchTx, filterTxStatus, filterTxCat, fetchTransactions]);
  useEffect(() => { fetchWallets(walletPage, filterWalletStatus); }, [walletPage, filterWalletStatus, fetchWallets]);
  useEffect(() => { fetchDisputes(filterDisputeStatus); }, [filterDisputeStatus, fetchDisputes]);

  const approveRecharge = async (txId) => {
    try {
      await adminApi.patch(`/wallet/admin/transactions/${txId}`, null, { params: { status:'completed', notes:'Recarga aprobada por administrador' } });
      toast.success('Recarga aprobada');
      fetchTransactions(txPage, searchTx, filterTxStatus, filterTxCat);
      fetchOverview();
    } catch(e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await adminApi.put('/wallet/admin/settings', editSettings);
      setSettings(r.data);
      toast.success('Configuración guardada');
    } catch(e) { toast.error('Error al guardar'); }
    finally { setSavingSettings(false); }
  };

  const setS = (key, val) => setEditSettings(p => ({ ...p, [key]: val }));

  const tableHead = (cols) => (
    <div style={{ display:'grid', gridTemplateColumns:cols, gap:12, padding:'10px 16px', background:'#f9fafb', borderRadius:'10px 10px 0 0', fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase' }}>
      {/* rendered by parent */}
    </div>
  );

  if (loading) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f9fafb' }}>
        <Loader2 size={32} color="#f97316" className="animate-spin"/>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', background:'#f8fafc', fontFamily:"'Inter',sans-serif" }}>
      {/* Header */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e5e7eb', padding:'16px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={() => navigate('/admin/dashboard')} style={{ background:'#f3f4f6', border:'none', borderRadius:8, padding:'8px 12px', cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontWeight:600, fontSize:13 }}><ChevronLeft size={15}/>Admin</button>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#f97316,#fbbf24)', display:'flex', alignItems:'center', justifyContent:'center' }}><Wallet size={18} color="#fff"/></div>
            <div>
              <h1 style={{ fontWeight:900, fontSize:18, margin:0, color:'#111827' }}>OkWallet Admin</h1>
              <p style={{ margin:0, fontSize:11, color:'#9ca3af' }}>Panel de administración de carteras digitales</p>
            </div>
          </div>
        </div>
        <button onClick={loadAll} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', background:'#f3f4f6', border:'none', borderRadius:10, fontWeight:600, fontSize:13, cursor:'pointer', color:'#374151' }}><RefreshCw size={14}/>Actualizar</button>
      </div>

      {/* Tabs */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e5e7eb', padding:'0 24px' }}>
        <div style={{ display:'flex', gap:2 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding:'14px 18px', border:'none', background:'transparent', cursor:'pointer', fontWeight:tab===t.id?700:500, fontSize:13, color:tab===t.id?'#f97316':'#6b7280', borderBottom:`2px solid ${tab===t.id?'#f97316':'transparent'}`, display:'flex', alignItems:'center', gap:6, transition:'all 0.15s' }}>
              <t.icon size={14}/> {t.label}
              {t.id === 'disputes' && (overview?.disputes?.open || 0) > 0 && (
                <span style={{ background:'#ef4444', color:'#fff', borderRadius:20, fontSize:10, fontWeight:800, padding:'0 6px', lineHeight:'18px' }}>{overview.disputes.open}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding:'24px', maxWidth:1200, margin:'0 auto' }}>

        {/* ── DASHBOARD ── */}
        {tab === 'dashboard' && overview && (
          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:16 }}>
              <StatCard label="Wallets activas" value={overview.wallets?.active || 0} sub={`${overview.wallets?.total || 0} total · ${overview.wallets?.frozen || 0} congeladas`} icon={Wallet} color="#f97316" bg="#fff7ed"/>
              <StatCard label="Saldo total en sistema" value={fmt(overview.balances?.total_in_system)} sub="MXN disponible" icon={DollarSign} color="#22c55e" bg="#f0fdf4"/>
              <StatCard label="Volumen 30 días" value={fmt(overview.volume_30d)} sub="Transferencias + recargas" icon={TrendingUp} color="#8b5cf6" bg="#f5f3ff"/>
              <StatCard label="Comisiones 30 días" value={fmt(overview.fees_30d)} sub="Ingresos plataforma" icon={BarChart3} color="#06b6d4" bg="#ecfeff"/>
              <StatCard label="Transacciones totales" value={overview.transactions?.total || 0} sub={`${overview.transactions?.pending || 0} pendientes · ${overview.transactions?.disputed || 0} disputadas`} icon={Activity} color="#374151" bg="#f9fafb"/>
              <StatCard label="Disputas abiertas" value={overview.disputes?.open || 0} sub={`${overview.disputes?.under_review || 0} en revisión`} icon={AlertTriangle} color="#ef4444" bg="#fef2f2"/>
            </div>

            {/* Pending recharges */}
            <div>
              <h3 style={{ fontWeight:800, fontSize:16, color:'#111827', marginBottom:14 }}>⏳ Recargas pendientes de aprobación</h3>
              {transactions.filter(t=>t.category==='recharge'&&t.status==='pending').length === 0 ? (
                <div style={{ textAlign:'center', padding:'28px', background:'#f9fafb', borderRadius:14, color:'#9ca3af', fontSize:13 }}>No hay recargas pendientes</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {transactions.filter(t=>t.category==='recharge'&&t.status==='pending').map(tx => (
                    <div key={tx.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto auto', gap:12, alignItems:'center', padding:'14px 18px', background:'#fff', borderRadius:12, border:'1px solid #fef3c7', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:13 }}>{tx.restaurant_id}</div>
                        <div style={{ fontSize:11, color:'#9ca3af' }}>{fmtDate(tx.created_at)}</div>
                      </div>
                      <div style={{ fontWeight:800, fontSize:16, color:'#22c55e' }}>{fmt(tx.amount)}</div>
                      <div>
                        <div style={{ fontSize:12, color:'#6b7280', textTransform:'uppercase', fontWeight:700 }}>{tx.payment_method}</div>
                        <div style={{ fontSize:11, color:'#9ca3af', fontFamily:'monospace' }}>{tx.reference}</div>
                      </div>
                      <StatusBadge s={tx.status}/>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => approveRecharge(tx.id)} style={{ padding:'8px 14px', background:'#22c55e', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}><Check size={12}/>Aprobar</button>
                        <button onClick={async () => { await adminApi.patch(`/wallet/admin/transactions/${tx.id}`, null, {params:{status:'failed',notes:'Rechazado por administrador'}}); fetchTransactions(); }} style={{ padding:'8px 12px', background:'#fef2f2', color:'#ef4444', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}><X size={14}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TRANSACTIONS ── */}
        {tab === 'transactions' && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {/* Filters */}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, minWidth:220, background:'#fff', border:'1.5px solid #e5e7eb', borderRadius:10, padding:'8px 14px' }}>
                <Search size={14} color="#9ca3af"/>
                <input value={searchTx} onChange={e=>setSearchTx(e.target.value)} placeholder="Buscar referencia, descripción, restaurante..." style={{ border:'none', outline:'none', flex:1, fontSize:13, background:'transparent' }} />
              </div>
              <select value={filterTxStatus} onChange={e=>setFilterTxStatus(e.target.value)} style={{ padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:13, background:'#fff', cursor:'pointer', outline:'none' }}>
                <option value="">Todos los estados</option>
                {['completed','pending','failed','disputed','reversed'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filterTxCat} onChange={e=>setFilterTxCat(e.target.value)} style={{ padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:13, background:'#fff', cursor:'pointer', outline:'none' }}>
                <option value="">Todas las categorías</option>
                {['recharge','transfer_in','transfer_out','fee','refund','adjustment'].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
              <span style={{ fontSize:12, color:'#9ca3af', flexShrink:0 }}>{totalTxs} registros</span>
            </div>

            {/* Table */}
            <div style={{ background:'#fff', borderRadius:14, border:'1px solid #f3f4f6', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 80px', gap:12, padding:'12px 18px', background:'#f9fafb', fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase' }}>
                {['Descripción / Ref.','Monto','Comisión','Categoría','Estado','Acción'].map(h=><span key={h}>{h}</span>)}
              </div>
              {transactions.length === 0 ? (
                <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>No hay transacciones</div>
              ) : transactions.map(tx => (
                <div key={tx.id} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 80px', gap:12, padding:'13px 18px', borderTop:'1px solid #f9fafb', alignItems:'center', fontSize:13 }}>
                  <div>
                    <div style={{ fontWeight:600, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tx.description || '—'}</div>
                    <div style={{ fontSize:11, color:'#9ca3af', fontFamily:'monospace' }}>{tx.reference} · {tx.restaurant_id}</div>
                    <div style={{ fontSize:10, color:'#d1d5db' }}>{fmtDate(tx.created_at)}</div>
                  </div>
                  <div style={{ fontWeight:800, fontSize:14, color:tx.type==='credit'?'#22c55e':'#ef4444' }}>{tx.type==='credit'?'+':'-'}{fmt(tx.amount)}</div>
                  <div style={{ color:'#9ca3af', fontSize:12 }}>{tx.fee > 0 ? fmt(tx.fee) : '—'}</div>
                  <div><span style={{ padding:'2px 8px', borderRadius:10, background:'#f3f4f6', fontSize:11, fontWeight:600, color:'#374151' }}>{tx.category}</span></div>
                  <StatusBadge s={tx.status}/>
                  <div style={{ display:'flex', gap:4 }}>
                    {tx.status === 'pending' && tx.category === 'recharge' && (
                      <button onClick={() => approveRecharge(tx.id)} style={{ padding:'5px 8px', background:'#f0fdf4', color:'#22c55e', border:'none', borderRadius:6, cursor:'pointer' }} title="Aprobar"><Check size={13}/></button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div style={{ display:'flex', justifyContent:'center', gap:8 }}>
              <button disabled={txPage===0} onClick={()=>setTxPage(p=>p-1)} style={{ padding:'8px 16px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:txPage===0?'not-allowed':'pointer', opacity:txPage===0?0.4:1, fontWeight:600, fontSize:13 }}>← Anterior</button>
              <span style={{ padding:'8px 16px', fontSize:13, color:'#6b7280' }}>Pág. {txPage+1} / {Math.max(1,Math.ceil(totalTxs/PAGE_SIZE))}</span>
              <button disabled={txPage>=Math.ceil(totalTxs/PAGE_SIZE)-1} onClick={()=>setTxPage(p=>p+1)} style={{ padding:'8px 16px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:'pointer', opacity:txPage>=Math.ceil(totalTxs/PAGE_SIZE)-1?0.4:1, fontWeight:600, fontSize:13 }}>Siguiente →</button>
            </div>
          </div>
        )}

        {/* ── WALLETS ── */}
        {tab === 'wallets' && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
              <select value={filterWalletStatus} onChange={e=>setFilterWalletStatus(e.target.value)} style={{ padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:13, background:'#fff', cursor:'pointer', outline:'none' }}>
                <option value="">Todos los estados</option>
                {['active','frozen','suspended'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <span style={{ fontSize:12, color:'#9ca3af' }}>{totalWallets} wallets</span>
            </div>

            <div style={{ background:'#fff', borderRadius:14, border:'1px solid #f3f4f6', overflow:'hidden' }}>
              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 100px', gap:12, padding:'12px 18px', background:'#f9fafb', fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase' }}>
                {['Wallet / Restaurante','Saldo','Enviado','Recibido','Estado','Acciones'].map(h=><span key={h}>{h}</span>)}
              </div>
              {wallets.length === 0 ? (
                <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>No hay wallets</div>
              ) : wallets.map(w => (
                <div key={w.id} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 100px', gap:12, padding:'13px 18px', borderTop:'1px solid #f9fafb', alignItems:'center', fontSize:13 }}>
                  <div>
                    <div style={{ fontWeight:700, color:'#111827' }}>{w.restaurant_id}</div>
                    <div style={{ fontSize:10, fontFamily:'monospace', color:'#d1d5db' }}>{w.id?.slice(0,16)}...</div>
                    <div style={{ fontSize:10, color:'#9ca3af' }}>Creado: {fmtDate(w.created_at)}</div>
                  </div>
                  <div style={{ fontWeight:800, color:'#111827' }}>{fmt(w.balance)}</div>
                  <div style={{ color:'#6b7280', fontSize:12 }}>{fmt(w.total_sent)}</div>
                  <div style={{ color:'#6b7280', fontSize:12 }}>{fmt(w.total_received)}</div>
                  <StatusBadge s={w.status}/>
                  <button onClick={() => setWalletAction(w)} style={{ padding:'6px 12px', background:'#f3f4f6', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:12, display:'flex', alignItems:'center', gap:4 }}><Edit2 size={12}/>Gestionar</button>
                </div>
              ))}
            </div>

            <div style={{ display:'flex', justifyContent:'center', gap:8 }}>
              <button disabled={walletPage===0} onClick={()=>setWalletPage(p=>p-1)} style={{ padding:'8px 16px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:walletPage===0?'not-allowed':'pointer', opacity:walletPage===0?0.4:1, fontWeight:600, fontSize:13 }}>← Anterior</button>
              <span style={{ padding:'8px 16px', fontSize:13, color:'#6b7280' }}>Pág. {walletPage+1} / {Math.max(1,Math.ceil(totalWallets/PAGE_SIZE))}</span>
              <button disabled={walletPage>=Math.ceil(totalWallets/PAGE_SIZE)-1} onClick={()=>setWalletPage(p=>p+1)} style={{ padding:'8px 16px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:'pointer', opacity:walletPage>=Math.ceil(totalWallets/PAGE_SIZE)-1?0.4:1, fontWeight:600, fontSize:13 }}>Siguiente →</button>
            </div>
          </div>
        )}

        {/* ── DISPUTES ── */}
        {tab === 'disputes' && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              <select value={filterDisputeStatus} onChange={e=>setFilterDisputeStatus(e.target.value)} style={{ padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:13, background:'#fff', cursor:'pointer', outline:'none' }}>
                <option value="">Todas</option>
                {['open','under_review','resolved','rejected'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <span style={{ fontSize:12, color:'#9ca3af' }}>{totalDisputes} disputas</span>
            </div>

            {disputes.length === 0 ? (
              <div style={{ textAlign:'center', padding:'48px', background:'#f9fafb', borderRadius:14 }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🎉</div>
                <p style={{ fontWeight:700, color:'#374151', marginBottom:4 }}>Sin disputas abiertas</p>
                <p style={{ color:'#9ca3af', fontSize:13 }}>No hay disputas en el estado seleccionado.</p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {disputes.map(d => (
                  <div key={d.id} style={{ background:'#fff', borderRadius:14, padding:'18px 20px', border:`1px solid ${d.status==='open'?'#fca5a5':d.status==='under_review'?'#fde68a':'#e5e7eb'}`, boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:16, alignItems:'start' }}>
                      <div>
                        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
                          <StatusBadge s={d.status}/>
                          <span style={{ fontSize:12, fontWeight:700, color:'#374151', textTransform:'capitalize' }}>{d.dispute_type?.replace('_',' ')}</span>
                          <span style={{ fontSize:11, fontFamily:'monospace', color:'#d1d5db' }}>#{d.id?.slice(0,8)}</span>
                        </div>
                        <p style={{ margin:0, fontSize:13, color:'#374151', marginBottom:6 }}>{d.description}</p>
                        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                          <span style={{ fontSize:11, color:'#9ca3af' }}>Restaurante: <strong>{d.restaurant_id}</strong></span>
                          <span style={{ fontSize:11, color:'#9ca3af' }}>Monto: <strong style={{ color:'#ef4444' }}>{fmt(d.transaction_amount)}</strong></span>
                          <span style={{ fontSize:11, color:'#9ca3af' }}>Abierta: {fmtDate(d.created_at)}</span>
                          {d.resolved_at && <span style={{ fontSize:11, color:'#9ca3af' }}>Resuelta: {fmtDate(d.resolved_at)}</span>}
                        </div>
                        {d.resolution_notes && <div style={{ marginTop:8, padding:'8px 12px', background:'#f0fdf4', borderRadius:8, fontSize:12, color:'#15803d' }}>✅ {d.resolution_notes}</div>}
                      </div>
                      {(d.status === 'open' || d.status === 'under_review') && (
                        <button onClick={() => setResolveDispute(d)} style={{ padding:'10px 18px', background:'#f97316', color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:13, whiteSpace:'nowrap' }}>Resolver</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── SETTINGS ── */}
        {tab === 'settings' && editSettings && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, maxWidth:800 }}>
            {/* Transfer fees */}
            <div style={{ background:'#fff', borderRadius:16, padding:'20px 22px', border:'1px solid #f3f4f6', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <h3 style={{ fontWeight:800, fontSize:15, color:'#111827', marginBottom:16, display:'flex', alignItems:'center', gap:8 }}><ArrowUpRight size={16} color="#f97316"/> Comisiones de transferencia</h3>
              {[
                { key:'user_to_user_fee_pct', label:'Usuario → Usuario', unit:'%' },
                { key:'user_to_business_fee_pct', label:'Usuario → Negocio', unit:'%' },
                { key:'business_to_user_fee_pct', label:'Negocio → Usuario', unit:'%' },
                { key:'business_to_business_fee_pct', label:'Negocio → Negocio', unit:'%' },
              ].map(f => (
                <div key={f.key} style={{ marginBottom:14 }}>
                  <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>{f.label}</label>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input type="number" step="0.1" min="0" max="20" value={editSettings[f.key] ?? ''} onChange={e=>setS(f.key,parseFloat(e.target.value))}
                      style={{ flex:1, padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, fontWeight:700, outline:'none' }} />
                    <span style={{ fontSize:13, fontWeight:700, color:'#9ca3af', width:20 }}>{f.unit}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Recharge fees */}
            <div style={{ background:'#fff', borderRadius:16, padding:'20px 22px', border:'1px solid #f3f4f6', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <h3 style={{ fontWeight:800, fontSize:15, color:'#111827', marginBottom:16, display:'flex', alignItems:'center', gap:8 }}><Plus size={16} color="#22c55e"/> Comisiones de recarga</h3>
              {[
                { key:'recharge_stripe_fee_pct', label:'Stripe (tarjeta)', unit:'%' },
                { key:'recharge_spei_fee_fixed', label:'SPEI (fijo MXN)', unit:'$' },
                { key:'recharge_crypto_fee_pct', label:'Criptomonedas', unit:'%' },
                { key:'recharge_cash_fee_fixed', label:'Efectivo (fijo MXN)', unit:'$' },
              ].map(f => (
                <div key={f.key} style={{ marginBottom:14 }}>
                  <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>{f.label}</label>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input type="number" step="0.1" min="0" value={editSettings[f.key] ?? ''} onChange={e=>setS(f.key,parseFloat(e.target.value))}
                      style={{ flex:1, padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, fontWeight:700, outline:'none' }} />
                    <span style={{ fontSize:13, fontWeight:700, color:'#9ca3af', width:20 }}>{f.unit}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Limits */}
            <div style={{ background:'#fff', borderRadius:16, padding:'20px 22px', border:'1px solid #f3f4f6', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <h3 style={{ fontWeight:800, fontSize:15, color:'#111827', marginBottom:16, display:'flex', alignItems:'center', gap:8 }}><Shield size={16} color="#8b5cf6"/> Límites</h3>
              {[
                { key:'min_recharge', label:'Recarga mínima (MXN)' },
                { key:'max_recharge', label:'Recarga máxima (MXN)' },
                { key:'min_transfer', label:'Transferencia mínima (MXN)' },
                { key:'max_transfer_daily', label:'Límite diario de transferencias (MXN)' },
                { key:'max_balance', label:'Saldo máximo permitido (MXN)' },
              ].map(f => (
                <div key={f.key} style={{ marginBottom:14 }}>
                  <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>{f.label}</label>
                  <input type="number" step="100" min="0" value={editSettings[f.key] ?? ''} onChange={e=>setS(f.key,parseFloat(e.target.value))}
                    style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, fontWeight:700, outline:'none', boxSizing:'border-box' }} />
                </div>
              ))}
            </div>

            {/* Toggles */}
            <div style={{ background:'#fff', borderRadius:16, padding:'20px 22px', border:'1px solid #f3f4f6', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <h3 style={{ fontWeight:800, fontSize:15, color:'#111827', marginBottom:16, display:'flex', alignItems:'center', gap:8 }}><Activity size={16} color="#06b6d4"/> Métodos habilitados</h3>
              {[
                { key:'transfer_enabled', label:'Transferencias entre wallets' },
                { key:'recharge_stripe_enabled', label:'Recargas con Stripe' },
                { key:'recharge_spei_enabled', label:'Recargas con SPEI' },
                { key:'recharge_crypto_enabled', label:'Recargas con criptomonedas' },
                { key:'recharge_cash_enabled', label:'Recargas en efectivo' },
              ].map(f => (
                <label key={f.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, cursor:'pointer' }}>
                  <span style={{ fontSize:13, fontWeight:600, color:'#374151' }}>{f.label}</span>
                  <div onClick={() => setS(f.key, !editSettings[f.key])}
                    style={{ width:44, height:24, borderRadius:12, background:editSettings[f.key]?'#f97316':'#e5e7eb', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                    <div style={{ width:18, height:18, borderRadius:'50%', background:'#fff', position:'absolute', top:3, left:editSettings[f.key]?22:4, transition:'left 0.2s', boxShadow:'0 1px 4px rgba(0,0,0,0.2)' }}/>
                  </div>
                </label>
              ))}
            </div>

            {/* Save button */}
            <div style={{ gridColumn:'span 2', display:'flex', justifyContent:'flex-end' }}>
              <button onClick={saveSettings} disabled={savingSettings}
                style={{ padding:'14px 32px', background:'#f97316', color:'#fff', border:'none', borderRadius:12, fontWeight:800, fontSize:15, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
                {savingSettings ? <Loader2 size={18} className="animate-spin"/> : <Check size={18}/>} Guardar configuración
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {resolveDispute && <ResolveDisputeModal dispute={resolveDispute} onClose={() => setResolveDispute(null)} onSuccess={() => { fetchDisputes(filterDisputeStatus); fetchOverview(); }} />}
      {walletAction && <WalletActionModal wallet={walletAction} onClose={() => setWalletAction(null)} onSuccess={() => fetchWallets(walletPage, filterWalletStatus)} />}
    </div>
  );
}
